<script>
	import { onMount } from 'svelte';

	// Props
	export let product = {};
	export let open = false;
	export let showStats = true;

	// Sample statistics (in a real app, these would come from props or API)
	let stats = {
		views: 1245,
		conversionRate: 3.2,
		clickThroughRate: 5.8,
		averageTimeViewed: '1m 32s'
	};

	// Close modal when clicking outside or pressing escape
	function handleOutsideClick(event) {
		if (event.target.classList.contains('modal-overlay')) {
			closeModal();
		}
	}

	function handleKeydown(event) {
		if (event.key === 'Escape') {
			closeModal();
		}
	}

	function closeModal() {
		open = false;
	}

	// Parse HTML content from product description
	$: description = product?.description ? product.description : '';

	// Format price with currency symbol
	$: formattedPrice = product?.price?.eur ? `€${product.price.eur.toFixed(2)}` : '';

	// Check if product is in stock
	$: inStock = product?.stock > 0;

	// Check if product is amplified
	$: amplified = product?.amplified || false;

	// Check if product is selected (assuming this is a property on the product)
	$: selected = product?.selected || false;

	onMount(() => {
		// Add event listener for escape key
		window.addEventListener('keydown', handleKeydown);

		return () => {
			window.removeEventListener('keydown', handleKeydown);
		};
	});
</script>

<svelte:window on:keydown={handleKeydown} />

{#if open && product}
	<div class="modal-overlay" on:click={handleOutsideClick}>
		<div class="modal" role="dialog" aria-modal="true">
			<button class="close-button" on:click={closeModal} aria-label="Close modal">
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="24"
					height="24"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					stroke-linecap="round"
					stroke-linejoin="round"
				>
					<line x1="18" y1="6" x2="6" y2="18" />
					<line x1="6" y1="6" x2="18" y2="18" />
				</svg>
			</button>

			<div class="modal-content">
				<div class="product-grid">
					<!-- Product Image -->
					<div class="product-image">
						{#if product.image}
							<img src={product.image || '/placeholder.svg'} alt={product.title} />
						{:else}
							<div class="placeholder-image">No image available</div>
						{/if}
					</div>

					<!-- Product Details -->
					<div class="product-details">
						<div class="product-header">
							<h2>{product.title}</h2>

							<div class="product-badges">
								{#if selected}
									<span class="badge selected-badge">Selected</span>
								{/if}
								{#if amplified}
									<span class="badge amplified-badge">Amplified</span>
								{/if}
							</div>
						</div>

						<div class="price-stock">
							<span class="price">{formattedPrice}</span>
							<span class="stock-badge" class:in-stock={inStock} class:out-of-stock={!inStock}>
								{inStock ? `In Stock (${product.stock})` : 'Out of Stock'}
							</span>
						</div>
						<h3>Metadata</h3>

						<div class="product-meta">
							{#if product.sku}
								<div class="meta-item">
									<span class="meta-label">SKU:</span>
									<span class="meta-value">{product.sku}</span>
								</div>
							{/if}

							{#if product.id}
								<div class="meta-item">
									<span class="meta-label">ID:</span>
									<span class="meta-value">{product.id}</span>
								</div>
							{/if}

							{#if product.brand}
								<div class="meta-item">
									<span class="meta-label">Brand:</span>
									<span class="meta-value">{product.brand}</span>
								</div>
							{/if}

							{#if product.category_list && product.category_list.length > 0}
								<div class="meta-item">
									<span class="meta-label">Categories:</span>
									<span class="meta-value">{product.category_list.join(', ')}</span>
								</div>
							{/if}

							{#if product.created_at}
								<div class="meta-item">
									<span class="meta-label">Created:</span>
									<span class="meta-value">{new Date(product.created_at).toLocaleDateString()}</span
									>
								</div>
							{/if}

							{#if product.updated_at}
								<div class="meta-item">
									<span class="meta-label">Updated:</span>
									<span class="meta-value">{new Date(product.updated_at).toLocaleDateString()}</span
									>
								</div>
							{/if}
						</div>

						{#if description}
							<div class="description">
								<h3>Description</h3>
								<div class="description-container">
									{@html description}
								</div>
							</div>
						{/if}
					</div>
				</div>

				<!-- Performance Statistics -->
				{#if showStats}
					<div class="stats-section">
						<h3>Product Performance</h3>
						<div class="stats-grid">
							<div class="stat-card">
								<div class="stat-value">{stats.views.toLocaleString()}</div>
								<div class="stat-label">Product Views</div>
							</div>

							<div class="stat-card">
								<div class="stat-value">{stats.conversionRate}%</div>
								<div class="stat-label">Conversion Rate</div>
							</div>

							<div class="stat-card">
								<div class="stat-value">{stats.clickThroughRate}%</div>
								<div class="stat-label">Click-Through Rate</div>
							</div>

							<div class="stat-card">
								<div class="stat-value">{stats.averageTimeViewed}</div>
								<div class="stat-label">Avg. Time Viewed</div>
							</div>
						</div>
					</div>
				{/if}

				<!-- Related Products Section -->
				{#if product.bought_together && product.bought_together.length > 0}
					<div class="related-section">
						<h3>Frequently Bought Together</h3>
						<div class="related-skus">
							{#each product.bought_together as sku}
								<div class="related-sku">{sku}</div>
							{/each}
						</div>
					</div>
				{/if}

				{#if product.bundle && product.bundle.length > 0}
					<div class="related-section">
						<h3>Bundle Products</h3>
						<div class="related-skus">
							{#each product.bundle as sku}
								<div class="related-sku">{sku}</div>
							{/each}
						</div>
					</div>
				{/if}
			</div>
		</div>
	</div>
{/if}

<style>
	/* Modal styles */
	.modal-overlay {
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background-color: rgba(0, 0, 0, 0.5);
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 9999; /* Increased z-index */
		padding: 1rem;
	}

	.modal {
		background-color: var(--white);
		border-radius: 0.5rem;
		width: 100%;
		max-width: 900px;
		max-height: 60vh;
		overflow-y: auto;
		position: relative;
		box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
		z-index: 10000; /* Increased z-index */
	}

	.close-button {
		position: absolute;
		top: 1rem;
		right: 1rem;
		background: transparent;
		border: none;
		cursor: pointer;
		color: var(--grey-600);
		z-index: 10001; /* Increased z-index */
		display: flex;
		align-items: center;
		justify-content: center;
		width: 2rem;
		height: 2rem;
		border-radius: 50%;
		transition: background-color 0.2s;
	}

	.close-button:hover {
		background-color: var(--grey-200);
	}

	.modal-content {
		padding: 2rem;
	}

	/* Product grid layout */
	.product-grid {
		display: grid;
		grid-template-columns: 1fr;
		gap: 2rem;
		margin-bottom: 2rem;
	}

	@media (min-width: 768px) {
		.product-grid {
			grid-template-columns: 200px 1fr;
		}
	}

	/* Product image */
	.product-image {
		display: flex;
		align-items: center;
		justify-content: center;
		background-color: var(--grey-50);
		border-radius: 0.5rem;
		overflow: hidden;
		aspect-ratio: 1 / 1;
	}

	.product-image img {
		max-width: 100%;
		max-height: 100%;
		object-fit: contain;
	}

	.placeholder-image {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 100%;
		height: 100%;
		color: var(--grey-600);
		font-size: 0.875rem;
	}

	/* Product details */
	.product-header {
		display: flex;
		flex-direction: column;
		gap: 1rem;
		margin-bottom: 1rem;
	}

	@media (min-width: 768px) {
		.product-header {
			flex-direction: row;
			justify-content: space-between;
			align-items: flex-start;
		}
	}

	.product-details h2 {
		font-size: 1.5rem;
		font-weight: 600;
		margin: 0;
		color: var(--accent);
	}

	/* Product badges */
	.product-badges {
		display: flex;
		gap: 0.5rem;
		flex-wrap: wrap;
	}

	.badge {
		display: inline-block;
		padding: 0.25rem 0.75rem;
		border-radius: 9999px;
		font-size: 0.75rem;
		font-weight: 500;
	}

	.selected-badge {
		background-color: var(--grey-200);
		color: var(--grey-600);
	}

	.amplified-badge {
		background-color: var(--accent);
		color: var(--white);
	}

	.price-stock {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 1rem;
		margin-bottom: 1.5rem;
	}

	.price {
		font-size: 1.5rem;
		font-weight: 600;
		color: var(--accent);
	}

	.stock-badge {
		display: inline-block;
		padding: 0.25rem 0.75rem;
		border-radius: 9999px;
		font-size: 0.75rem;
		font-weight: 500;
	}

	.in-stock {
		background-color: #dcfce7;
		color: #166534;
	}

	.out-of-stock {
		background-color: #fee2e2;
		color: #b91c1c;
	}

	/* Product metadata */
	.product-meta {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 0.5rem;
		margin-bottom: 1.5rem;
		font-size: 0.875rem;
		padding: 0.5em;
		border-radius: 0.5em;
		border: 1px solid var(--grey-200);
	}

	.meta-item {
		background-color: var(--grey-50);
		border-radius: 0.25rem;
		padding: 0.5rem 0.75rem;
		font-size: 0.65rem;
		color: var(--accent);
		font-family: monospace;
	}

	.meta-label {
		font-weight: 500;
		color: var(--grey-600);
		width: 6rem;
		flex-shrink: 0;
	}

	.meta-value {
		color: var(--accent);
	}

	/* Description */
	.description {
		margin-bottom: 1.5rem;
	}
	.description-container {
		height: 100px;
		overflow: scroll;
		border: 1px solid var(--grey-200);
		padding: 0.5em;
		border-radius: 0.5em;
	}

	h3 {
		font-size: 1rem;
		font-weight: 600;
		margin: 0 0 0.75rem 0;
		color: var(--accent);
	}

	.description :global(p) {
		margin: 0;
		font-size: 0.875rem;
		line-height: 1.5;
		color: var(--grey-600);
	}

	/* Stats section */
	.stats-section {
		border-top: 1px solid var(--grey-200);
		padding-top: 2rem;
		margin-bottom: 2rem;
	}

	.stats-section h3 {
		font-size: 1.25rem;
		font-weight: 600;
		margin: 0 0 1.5rem 0;
		color: var(--accent);
	}

	.stats-grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
		gap: 1rem;
	}

	.stat-card {
		background-color: var(--grey-50);
		border-radius: 0.5rem;
		padding: 1.25rem;
		text-align: center;
	}

	.stat-value {
		font-size: 1.5rem;
		font-weight: 600;
		color: var(--accent);
		margin-bottom: 0.5rem;
	}

	.stat-label {
		font-size: 0.75rem;
		color: var(--grey-600);
	}

	/* Related products section */
	.related-section {
		border-top: 1px solid var(--grey-200);
		padding-top: 2rem;
		margin-bottom: 2rem;
	}

	.related-section h3 {
		font-size: 1.25rem;
		font-weight: 600;
		margin: 0 0 1.5rem 0;
		color: var(--accent);
	}

	.related-skus {
		display: flex;
		flex-wrap: wrap;
		gap: 0.5rem;
	}

	.related-sku {
		background-color: var(--grey-50);
		border-radius: 0.25rem;
		padding: 0.5rem 0.75rem;
		font-size: 0.75rem;
		color: var(--accent);
		font-family: monospace;
	}
</style>
